import { roleService } from '@/helpers/services/role.service'
import i18n from '@/i18n'
import { filtersHelper, sharedHelper } from '@/utils'
import {
  LISTING_STATE,
  listingMutations,
  listAction,
  detailAction,
  writeAction,
  createdMsg,
  updatedMsg,
  REQUESTING,
  SUCCESS,
  alertAndClear,
} from '@/state/listing.store'

export const ROLE_MODULE = 'role'

export const GET_ALL_ROLE = 'getAllRole'
export const GET_ROLE = 'getRole'
export const GET_PERMISSIONS = 'getPermissions'
export const CREATE_ROLE = 'createRole'
export const UPDATE_ROLE = 'updateRole'
export const UPDATE_ROLE_STATUS = 'updateRoleStatus'

const PERMISSIONS_SUCCESS = 'permissionsSuccess'

export const state = {
  ...LISTING_STATE(),
  /**
   * Every right in the system, grouped by module, for the role editor.
   *
   * Held in the store rather than fetched per form because both the editor and
   * the listing's permission filter need it, and it changes only when the
   * backend is redeployed.
   */
  permissions: [],
}

function mapRoleRow(item) {
  return {
    id: item.uuid,
    name: item.name,
    isActive: item.is_active,
    permissions: (item.permissions || []).map((p) => ({ code: p.code, name: p.name })),
    permissionCodes: (item.permissions || []).map((p) => p.code),
    permissionCount: (item.permissions || []).length,
    createdAt: filtersHelper.getDateFormat(item.created_at),
    updatedAt: filtersHelper.getDateFormat(item.updated_at),
  }
}

export const actions = {
  [GET_ALL_ROLE]: listAction((filter) => roleService.getAllRoles(filter)),

  [GET_ROLE]: detailAction(({ id }) => roleService.getRole(id)),

  [GET_PERMISSIONS]({ commit, state: current }) {
    // Already loaded: the editor and the filter both ask, and the answer only
    // changes on a redeploy.
    if (current.permissions.length) {
      return Promise.resolve({ data: current.permissions })
    }

    commit(REQUESTING, 'detail')

    return roleService
      .getPermissions()
      .then((result) => {
        commit(PERMISSIONS_SUCCESS, result?.data || [])
        commit(SUCCESS)
        return result
      })
      .catch((error) => alertAndClear(commit, error))
  },

  [CREATE_ROLE]: writeAction(
    ({ data }) => roleService.createRole(data),
    createdMsg('module.role'),
    GET_ALL_ROLE
  ),

  [UPDATE_ROLE]: writeAction(
    ({ id, data }) => roleService.updateRole(id, data),
    updatedMsg('module.role'),
    GET_ALL_ROLE
  ),

  [UPDATE_ROLE_STATUS]: writeAction(
    ({ id, isActive }) => roleService.updateRoleStatus(id, isActive),
    () => i18n.t('label.updateX', [i18n.t('label.status')]),
    GET_ALL_ROLE
  ),
}

export const mutations = {
  ...listingMutations(mapRoleRow),
  [PERMISSIONS_SUCCESS](state, groups) {
    state.permissions = (groups || []).map((group) => ({
      module: group.module,
      // Underscores are not a word. "READ_OWN" -> "Read Own",
      // which is the heading the editor shows.
      moduleLabel: humanise(group.module),
      permissions: group.permissions || [],
    }))
  },
}

/**
 * A permission module code as a person reads it.
 *
 * ORGANISATION is spelled out rather than title-cased, because the rest of the
 * app calls a client's site a BRAND — the picker, the user form, the listing
 * column — and only this screen said "Organisation". One thing with two names
 * is a thing people cannot find. The permission STRINGS keep their old spelling
 * (they are rows in the database and are matched by `$can('Read Organisation')`);
 * this is the label, not the identity.
 */
const MODULE_LABELS = {
  ORGANISATION: 'Brand',
}

function humanise(code) {
  if (sharedHelper.isNullOrEmpty(code)) return ''
  if (MODULE_LABELS[code]) return MODULE_LABELS[code]

  return String(code)
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}
